import { PSI_CONTRACTING_KEY } from './psi-contracting-query-key';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import apiClient from '@/lib/api/apiClient';
import {
  CompleteOnboardingRequest,
  CompleteOnboardingResponse,
  GetInvoiceForwardingAddressResponse,
  GetMeResponse,
  GetMonthlyInvestmentEmailPreferenceResponse,
  GetQuickGuideStatusResponse,
  UpdateMonthlyInvestmentEmailPreferenceRequest,
  UpdateMonthlyInvestmentEmailPreferenceResponse,
  UpdateMeRequest,
  UpdateMeResponse,
} from '@inversor-hub/types';

export const ME_QUERY_KEY = 'me';
export const SIGNATURE_QUERY_KEY = 'signature';
export const MONTHLY_INVESTMENT_EMAIL_PREFERENCE_QUERY_KEY = 'monthly-investment-email-preference';
export const INVOICE_FORWARDING_ADDRESS_QUERY_KEY = 'invoice-forwarding-address';

export function useGetMe() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [ME_QUERY_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<GetMeResponse>('/me');
      return data;
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
  });
}

export function useUpdateMe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: UpdateMeRequest) => {
      const { data } = await apiClient.patch<UpdateMeResponse>('/me', userData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ME_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SIGNATURE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PSI_CONTRACTING_KEY] });
    },
  });
}

async function uploadProfilePicture(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.put<{ url: string }>(`/me/profile-picture`, formData);
  return response.data.url;
}

export function useUploadProfilePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadProfilePicture,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ME_QUERY_KEY] });
    },
  });
}

export function useGetSignature() {
  return useQuery({
    queryKey: [SIGNATURE_QUERY_KEY],
    queryFn: async () => apiClient.get<{ signatureBase64?: string }>('/me/signature-base64'),
    select: (response) => response.data.signatureBase64,
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CompleteOnboardingRequest) => {
      const { data: res } = await apiClient.post<CompleteOnboardingResponse>(
        '/me/onboarding',
        data
      );
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ME_QUERY_KEY] });
    },
  });
}

export function useUploadSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (signatureBlob: Blob) => {
      const formData = new FormData();
      formData.append('file', signatureBlob, 'signature.png');
      await apiClient.put(`/me/signature`, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SIGNATURE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PSI_CONTRACTING_KEY] });
      queryClient.invalidateQueries({ queryKey: [ME_QUERY_KEY] });
    },
  });
}

export const QUICK_GUIDE_STATUS_KEY = 'quick-guide-status';

export function useMarkQuickGuideAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (actionId: string) => {
      await apiClient.post(`/me/quick-guide-actions/${actionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUICK_GUIDE_STATUS_KEY] });
    },
  });
}

export function useGetQuickGuideStatus(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [QUICK_GUIDE_STATUS_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<GetQuickGuideStatusResponse>('/me/quick-guide-status');
      return data;
    },
    enabled: isAuthenticated && (options?.enabled ?? true),
    staleTime: 60 * 1000,
  });
}

export function useGetInvoiceForwardingAddress(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [INVOICE_FORWARDING_ADDRESS_QUERY_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<GetInvoiceForwardingAddressResponse>(
        '/me/invoice-forwarding-address'
      );
      return data;
    },
    enabled: isAuthenticated && (options?.enabled ?? true),
    staleTime: 10 * 60 * 1000,
  });
}

export function useGetMonthlyInvestmentEmailPreference() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [MONTHLY_INVESTMENT_EMAIL_PREFERENCE_QUERY_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<GetMonthlyInvestmentEmailPreferenceResponse>(
        '/me/monthly-investment-email-preference'
      );
      return data;
    },
    enabled: isAuthenticated,
  });
}

export function useUpdateMonthlyInvestmentEmailPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateMonthlyInvestmentEmailPreferenceRequest) => {
      const { data } = await apiClient.patch<UpdateMonthlyInvestmentEmailPreferenceResponse>(
        '/me/monthly-investment-email-preference',
        request
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MONTHLY_INVESTMENT_EMAIL_PREFERENCE_QUERY_KEY],
      });
    },
  });
}
